import { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { createUser, updateUser } from '../../context/user/UserActions';
import { getFacilities } from '../../context/facility/FacilityActions';
import { getRoles } from '../../context/role/RoleActions';
import PhoneInput from 'react-phone-number-input';
import PasswordDialog from './PasswordDialog';
import { useAuth } from '../../context/AuthContext';


export default function UserDialog({ open, onClose, user, onSave }){
    const isCreate = !user;
    const [facilities, setFacilities] = useState([]);
    const [roles, setRoles] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [openPwd, setOpenPwd] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const {verifyAuth, hasRole, hasAnyRole} = useAuth();
    const isAdmin = hasRole && hasRole('ADMIN');
    const canViewRoles = hasAnyRole && hasAnyRole(['ADMIN', 'REGIONAL']);

    useEffect(() => {
      let mounted = true;
      (async () => {
        const [rFacilities, rRoles] = await Promise.all([
          getFacilities(),
          getRoles(),
        ]);
        if (!mounted) return;
        if (!rFacilities?.error) setFacilities(Array.isArray(rFacilities.data) ? rFacilities.data : []);
        if (!rRoles?.error) setRoles(Array.isArray(rRoles.data) ? rRoles.data : []);
      })();
      return () => { mounted = false; };
    }, []);

  if (!open) return null;
  const initialValues = {
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '',
    repeatPassword: '',
    facility: user?.facilityId || '',
    // normalize roles to an array of role ids (strings) for multi-select so existing roles are selected
    roles: (user?.roles && Array.isArray(user.roles) ? user.roles.map(r => String(typeof r === 'object' ? (r.id ?? r.roleId ?? r.role?.id) : r)) :
      (user?.role ? (Array.isArray(user.role) ? user.role.map(r => String(typeof r === 'object' ? (r.id ?? r.roleId ?? r.role?.id) : r)) : [String(typeof user.role === 'object' ? (user.role.id ?? user.role.roleId ?? user.role.role?.id) : user.role)]) :
      (user?.roleName ? [String(user.roleName)] : []))),
  };

  const phoneRegex = /^(?:\+234\d{10}|0\d{10})$/;

  const FACILITY_EXEMPT_ROLES = ['ADMIN', 'REGIONAL'];

  const roleNameFromSelection = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    const asStr = String(value);
    const byId = roles.find(r => String(r.id) === asStr);
    return String(byId?.name || asStr).toUpperCase();
  };

  // Facility is not applicable when ADMIN or REGIONAL is selected.
  const isFacilityApplicableForRoles = (selected = []) => {
    const normalized = Array.isArray(selected) ? selected : (selected ? [selected] : []);
    if (normalized.length === 0) return false;
    return !normalized.some(s => FACILITY_EXEMPT_ROLES.includes(roleNameFromSelection(s)));
  };

  const validationSchema = Yup.object({
    name: Yup.string().min(3, 'Name must be at least 3 characters').required('Please provide name'),
    email: Yup.string().email('Please provide a valid email').required('Please provide email'),
    phone: Yup.string().matches(phoneRegex, 'Please provide a valid phone number').required('Please provide phone'),
    password: Yup.string().when([], (v, schema) => isCreate ? Yup.string().min(6,'Password must be at least 6 characters').required('Please provide password') : Yup.string().min(6,'Password must be at least 6 characters')),
    repeatPassword: Yup.string().when('password', (pw, schema) => {
      if (isCreate && pw && pw.length > 0) return Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('Please repeat password');
      return Yup.string();
    }),
    facility: Yup.string().notRequired(),
    roles: canViewRoles
      ? Yup.array().of(Yup.string()).min(1, 'Please select role')
      : Yup.array().of(Yup.string()).notRequired(),
  });

  return (
    <dialog open className="modal">
      <div className="modal-box w-11/12 max-w-3xl">
        <h3 className="font-bold text-lg mb-4">{isCreate ? 'Create User' : `User #${user?.email}`}</h3>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, actions) => {
            actions.setSubmitting(true);
            setErrorMessage('');
            try{
              console.log(values);
              if (isCreate){
                const canKeepFacility = isFacilityApplicableForRoles(values.roles || []);
                const payload = { name: values.name, email: values.email, phone: values.phone, password: values.password };
                if (canViewRoles) payload.roles = values.roles || [];
                if (canKeepFacility && values.facility) {
                  payload.facility = values.facility;
                }
                console.log('Creating user with values:', payload);
                const res = await createUser(payload);
                if (res?.error) {
                  setErrorMessage(res.error || 'Failed to create user');
                } else {
                  alert('User created successfully');
                  const created = (res.data) ? res.data : payload;
                  onSave && onSave(created);
                }
              } else {
                const canKeepFacility = isFacilityApplicableForRoles(values.roles || []);
                const payload = { name: values.name, email: values.email, phone: values.phone };
                if (canViewRoles) payload.roles = values.roles || [];
                if (canKeepFacility && values.facility) {
                  payload.facilityId = values.facility;
                }
                // include password only if set
                console.log(payload);
                if (values.password) payload.password = values.password;
                const res = await updateUser(user.id, payload);
                if (res?.error) {
                  setErrorMessage(res.error || 'Failed to update user');
                } else {
                  alert('User updated successfully');
                  const updated = (res.data) ? res.data : { ...user, ...payload };
                  verifyAuth(); // refresh auth context in case current user updated self
                  onSave && onSave(updated);
                }
              }
            }catch(e){
              setErrorMessage(e?.message || 'Network error');
            }finally{
              actions.setSubmitting(false);
            }
          }}
        >
          {formik => (
            <Form>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <Field name="name" className="w-full px-3 py-2 border rounded" />
                  <ErrorMessage name="name" component="div" className="text-red-600 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <Field name="email" type="email" className="w-full px-3 py-2 border rounded" />
                  <ErrorMessage name="email" component="div" className="text-red-600 text-sm" />
                </div>

                <div className="phone-input-container">
                      <PhoneInput
                        className='text-3xl'
                        international
                        withCountryCallingCode
                        countryCallingCodeEditable={false}
                        defaultCountry="NG"
                        placeholder="Enter Phone Number"
                        name="phone"
                        value={formik.values.phone}
                        onChange={val => formik.setFieldValue('phone', val)}
                      />
                      {formik.errors.phone ? (
                        <div className='text-red-600 text-sm mt-1'>{formik.errors.phone}</div>
                      ) : null}
                    </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Facility</label>
                  {(() => {
                    const show = isFacilityApplicableForRoles(formik.values.roles);

                    if (!show) {
                      return (
                        <select disabled className="w-full px-3 py-2 border rounded bg-gray-100" aria-disabled>
                          <option value="">Not applicable for selected role(s)</option>
                        </select>
                      );
                    }
                    return (
                      <Field disabled={!isAdmin} name="facility" as="select" className="w-full px-3 py-2 border rounded">
                        <option value="">Select</option>
                        {facilities.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </Field>
                    );
                  })()}
                  <ErrorMessage name="facility" component="div" className="text-red-600 text-sm" />
                </div>

                {canViewRoles && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Roles</label>
                    <select
                      disabled={!isAdmin}
                      name="roles"
                      multiple
                      value={formik.values.roles}
                      onChange={e => {
                        const opts = Array.from(e.target.selectedOptions).map(o => o.value);
                        const canKeepFacility = isFacilityApplicableForRoles(opts);
                        formik.setFieldValue('roles', opts);
                        if (!canKeepFacility) {
                          formik.setFieldValue('facility', '');
                        }
                      }}
                      className="w-full h-32 px-3 py-2 border rounded"
                    >
                      {roles.map(r => (
                        <option key={r.id} value={String(r.id)}>{r.name}</option>
                      ))}
                    </select>
                    <ErrorMessage name="roles" component="div" className="text-red-600 text-sm" />
                  </div>
                )}

                {/* For create flow we still accept an initial password; for updates password changes use the dedicated dialog */}
                {isCreate && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Password</label>
                      <Field name="password" type="password" className="w-full px-3 py-2 border rounded" />
                      <ErrorMessage name="password" component="div" className="text-red-600 text-sm" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Repeat Password</label>
                      <Field name="repeatPassword" type="password" className="w-full px-3 py-2 border rounded" />
                      <ErrorMessage name="repeatPassword" component="div" className="text-red-600 text-sm" />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-3">
                {!isCreate && (
                  <button type="button" onClick={() => setOpenPwd(true)} className="btn btn-outline btn-sm mr-3">Change password</button>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
                <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>{isCreate ? 'Create' : 'Save'}</button>
              </div>

              {successMessage && <div className="mt-3 text-green-600 text-sm">{successMessage}</div>}

              {errorMessage && <div className="mt-3 text-red-600 text-sm">{errorMessage}</div>}
            </Form>
          )}
        </Formik>
        {/* Password dialog state & component rendered outside Formik's render */}
        <PasswordDialog open={openPwd} onClose={() => setOpenPwd(false)} userId={user?.id} onSuccess={() => { setSuccessMessage('Password updated'); setTimeout(()=>setSuccessMessage(''), 2000); }} />
      </div>
    </dialog>
  );
}
